import React, { useCallback, useMemo } from 'react';
import { Alert, Linking, Platform, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { WebView } from 'react-native-webview';
import type { ShouldStartLoadRequest } from 'react-native-webview/lib/WebViewTypes';

import { appTheme, useThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';

type Props = {
  html: string;
  style?: StyleProp<ViewStyle>;
};

function buildHealthInformationHtmlDocument(body_html: string, colors: ReturnType<typeof useThemeColors>, is_rtl: boolean): string {
  const text = colors?.text ?? '#111111';
  const text_secondary = colors?.textSecondary ?? '#666666';
  const primary = colors?.primary ?? '#007AFF';
  const surface = colors?.surfaceLight ?? '#F5F5F5';
  const border = colors?.border ?? '#E0E0E0';

  return `<!DOCTYPE html>
<html lang="${is_rtl ? 'ar' : 'en'}" dir="${is_rtl ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 15px;
      line-height: 1.55;
      color: ${text_secondary};
      background: transparent;
      -webkit-text-size-adjust: 100%;
    }
    h2 {
      font-size: 16px;
      font-weight: 700;
      color: ${text};
      margin: 20px 0 8px;
      letter-spacing: 0.2px;
    }
    h2:first-child { margin-top: 0; }
    p {
      margin: 0 0 12px;
    }
    strong, b {
      color: ${text};
      font-weight: 700;
    }
    a {
      color: ${primary};
      word-break: break-word;
    }
    ul, ol {
      margin: 0 0 12px;
      padding-inline-start: 20px;
    }
    li {
      margin-bottom: 14px;
    }
    blockquote {
      margin: 0 0 16px;
      padding: 12px 14px;
      border-radius: 14px;
      background: ${surface};
      border: 1px solid ${border};
    }
  </style>
</head>
<body>${body_html}</body>
</html>`;
}

export function HealthInformationCmsHtmlView({ html, style }: Props) {
  const colors = useThemeColors() ?? appTheme.colors;
  const { t } = useTranslation();
  const rtl = useRtl();

  const document_html = useMemo(
    () => buildHealthInformationHtmlDocument(html, colors, rtl.isRTL),
    [colors, html, rtl.isRTL],
  );

  const open_external_url = useCallback(
    async (url: string) => {
      try {
        const supported = await Linking.canOpenURL(url);
        if (supported) {
          await Linking.openURL(url);
        } else {
          Alert.alert(t('common.error'), t('healthInformation.cannotOpenLink'));
        }
      } catch {
        Alert.alert(t('common.error'), t('healthInformation.cannotOpenLink'));
      }
    },
    [t],
  );

  const on_should_start_load_with_request = useCallback(
    (request: ShouldStartLoadRequest) => {
      const url = request.url ?? '';
      if (url === 'about:blank' || url.startsWith('data:')) {
        return true;
      }
      if (/^https?:\/\//i.test(url)) {
        void open_external_url(url);
        return false;
      }
      return false;
    },
    [open_external_url],
  );

  if (!html.trim()) {
    return null;
  }

  return (
    <View style={[styles.wrap, style]}>
      <WebView
        originWhitelist={['*']}
        source={{ html: document_html, baseUrl: 'about:blank' }}
        style={styles.webview}
        scrollEnabled
        nestedScrollEnabled
        showsVerticalScrollIndicator={Platform.OS === 'android'}
        setSupportMultipleWindows={false}
        onShouldStartLoadWithRequest={on_should_start_load_with_request}
        javaScriptEnabled={false}
        domStorageEnabled={false}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    minHeight: 0,
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
});
